import { createStore } from '@oneappexchange/lwc-wired';
import { IS_DEVELOPMENT } from '~/config';
import { getVersionsByPackageId } from '~/modules/services/packagesService';
import { getSolutionsByType } from '~/modules/services/solutionsService';
import { handleException } from '~/modules/utilities/errorUtilities';
import { logger } from '~/modules/utilities/logger';
import { notifyFailure } from '~/modules/utilities/notificationUtilities';
import { TECHNOLOGY_SOLUTION_MAP, isApiSolutionType } from '~/modules/utilities/schemaUtilities';
import { OpsSecurityOpsReviewStatus } from '~/modules/utilities/securityReviewUtilities';
import { i18nStore } from '../i18nStore';
const initialState = {
  isLoading: false,
  solutionsData: [],
  versionsData: [],
  isLoadingVersions: false
};
export const listingSolutionsStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  fetchSolutions(listingType) {
    if (get().isLoading === true) {
      return Promise.resolve(false);
    }
    set(function resetAndBeginLoading() {
      return {
        ...initialState,
        isLoading: true
      };
    });
    try {
      const solutionType = TECHNOLOGY_SOLUTION_MAP[listingType];
      if (solutionType === 'force/Package') {
        return fetchPackages(set);
      }
      return fetchSolutions(solutionType, set);
    } catch (error) {
      this.reset();
      logger.error(`[LISTING_SOLUTIONS] There was a problem fetching solutions of type '${listingType}': ${error}`);
      notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
    }
    return Promise.resolve(false);
  },
  loadPackageVersionsForSolution(packageId) {
    if (get().isLoadingVersions) {
      return Promise.resolve();
    }
    set(function loadingVersions(draft) {
      draft.isLoadingVersions = true;
    });
    return fetchPackageVersions(packageId, set);
  }
}), 'LISTING_SOLUTIONS', IS_DEVELOPMENT);
async function fetchPackageVersions(packageId, set) {
  try {
    const allVersions = await getVersionsByPackageId(packageId);
    set(function setPackageVersions(draft) {
      draft.versionsData = draft.versionsData.filter(versions => versions.solutionKey !== packageId);
      draft.versionsData = draft.versionsData.concat(buildPackageSolutionVersion(allVersions));
    });
  } catch (err) {
    handleException(err, 'listingSolutionStore.fetchPackageVersions');
    notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
  } finally {
    set(function doneLoadingVersions(draft) {
      draft.isLoadingVersions = false;
    });
  }
}
async function fetchSolutions(solutionType, set) {
  const solutionsData = await getSolutionsByType(solutionType);
  set(function setSolutions(draft) {
    draft.solutionsData = solutionsData.map((solution, index) => {
      var _ref, _solution$id;
      return {
        value: (_ref = isApiSolutionType(solution.technology) ? solution.solution.name : solution.name) !== null && _ref !== void 0 ? _ref : String(index),
        key: (_solution$id = solution.id) !== null && _solution$id !== void 0 ? _solution$id : String(index),
        data: solution
      };
    });
    draft.versionsData = [];
    draft.isLoading = false;
  });
}
async function fetchPackages(set) {
  const solutionsPackages = await getSolutionsByType('force/Package');
  set(function setPackages(draft) {
    const solutionsData = [];
    const versionsData = [];
    for (const solution of solutionsPackages) {
      const solutionObj = solution.solution;
      const index = solutionsData.findIndex(sol => {
        return sol.key === solutionObj.packageId;
      });
      if (index === -1) {
        solutionsData.push({
          key: solutionObj.packageId,
          value: solutionObj.name,
          data: {
            id: solutionObj.packageId,
            packageName: solutionObj.name,
            packageCategory: solutionObj.packageCategory ? solutionObj.packageCategory : '',
            namespacePrefix: solutionObj.namespacePrefix ? solutionObj.namespacePrefix : ''
          }
        });
      }
      versionsData.push({
        solutionKey: solutionObj.packageId,
        value: i18nStore.getState().i18n.solutions.packageName({
          NAME: solutionObj.version.versionName,
          MAJOR: solutionObj.version.majorVersion,
          MINOR: solutionObj.version.minorVersion,
          PATCH: solutionObj.version.patchVersion
        }),
        key: solutionObj.version.packageVersionId,
        allPackageVersionInfo: solutionObj.version,
        securityReview: solution.securityReview,
        securityReviewFeedback: solution.securityReviewFeedback
      });
    }
    draft.solutionsData = solutionsData;
    draft.versionsData = versionsData;
    draft.isLoading = false;
  });
}
function buildPackageSolutionVersion(packageResponse) {
  return packageResponse.map(version => ({
    solutionKey: version.allPackageId,
    value: i18nStore.getState().i18n.solutions.packageName({
      NAME: version.versionName,
      MAJOR: version.majorVersion,
      MINOR: version.minorVersion,
      PATCH: version.patchVersion
    }),
    key: version.id,
    allPackageVersionInfo: {
      ...version,
      packageVersionId: version.id,
      majorVersion: version.majorVersion.toString(),
      minorVersion: version.minorVersion.toString(),
      patchVersion: version.patchVersion.toString()
    }
  }));
}
export function hasPackageVersionSecurityReviewed(state, packageVersionId) {
  const versionData = state.versionsData;
  for (const version of versionData) {
    if (version.key === packageVersionId) {
      return isPackageSolutionVersionReviewed(version);
    }
  }
  return false;
}
export function isPackageSolutionVersionReviewed(version) {
  if (version.allPackageVersionInfo.versionOptionsSecurityReviewed) {
    return true;
  }
  if (version.securityReview && version.securityReviewFeedback) {
    var _version$securityRevi;
    return Boolean(((_version$securityRevi = version.securityReviewFeedback) === null || _version$securityRevi === void 0 ? void 0 : _version$securityRevi.status) === OpsSecurityOpsReviewStatus.pass);
  }
  return false;
}
export function isSolutionSecurityReviewed(state, solutionId) {
  var _solutionsData$soluti, _currentSolution$secu;
  const solutionsData = state.solutionsData;
  const solutionIndex = solutionsData.findIndex(solution => solution.key === solutionId);
  const currentSolution = (_solutionsData$soluti = solutionsData[solutionIndex]) === null || _solutionsData$soluti === void 0 ? void 0 : _solutionsData$soluti.data;
  return Boolean(currentSolution && currentSolution.securityReviewFeedback && (currentSolution === null || currentSolution === void 0 ? void 0 : (_currentSolution$secu = currentSolution.securityReviewFeedback) === null || _currentSolution$secu === void 0 ? void 0 : _currentSolution$secu.status) === OpsSecurityOpsReviewStatus.pass);
}
export function selectSolutionsData(state) {
  return state.solutionsData;
}